import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import { useLazyGetTechnographicsCsvQuery, useLazyGetTechnographicsGoogleSheetsQuery } from 'services';
import { Pagination, useTableSelection, TableSelectionV2, Table } from 'features/table';
import { Button, SelectProfileTooltip, Text } from 'shared/components/ui';
import { useTechnographicsFilters } from 'features/search/hooks';
import { useTechnographicsSearch } from 'features/technographics';
import { ProgAIShortCompany, ProgAISearchCompanyResult, ProgAIResultsCompany } from 'shared/generated-models';
import { ResultsEmpty, ResultsError, ResultsSkeleton } from 'features/search/components';
import { CompanyModal } from 'features/technographics';
// import { Analytics } from 'features/analytics';
import { useIsFree } from 'features/auth';
import { TechnographicsExportToPushMenu } from 'features/integrations';
import { CompanyRow, getFiltersText } from 'features/technographics';

import styles from './index.module.scss';

interface Props {
	data: ProgAISearchCompanyResult;
	isFetching: boolean;
	isError: boolean;
	setProgress: React.Dispatch<React.SetStateAction<'results' | 'export'>>;
}

export const TechnographicsCompaniesResultsList = ({ data, isFetching, isError, setProgress }: Props) => {
	const [candidate, setCandidate] = useState<ProgAIResultsCompany | null>(null);

	const location = useLocation();

	const isFree = useIsFree();

	const search = useTechnographicsSearch();
	const filters = useTechnographicsFilters();
	const ref = useRef<HTMLDivElement>(null);

	const [getCsv] = useLazyGetTechnographicsCsvQuery();
	const [getGoogleSheetsUrl] = useLazyGetTechnographicsGoogleSheetsQuery();

	const closeCandidateModal = () => {
		setCandidate(null);
	};

	useEffect(() => {
		ref.current?.scroll({ top: 0, behavior: 'smooth' });
	}, [filters.page]);

	const {
		selectAll,
		isAllSelected,
		toggle,
		isSelected,
		someSelected,
		count,
		selectPage,
		selectCount,
		included,
		excluded,
		togglePage,
		clearSelection,
		selectedCount,
		mode
	} = useTableSelection<ProgAIShortCompany>({
		data: data?.results.map(row => row.profile),
		totalCount: data?.results_count,
		max: data?.results_count
	});

	useEffect(() => {
		if (location.state && location.state.refresh) {
			clearSelection();
		}
	}, [location.key]);

	const handleExportCsv = (isFull: boolean) => {
		// Analytics.trackCsvExport(count, 'Search');
		const { technographics_page, page, size, ...filteredFilters } = filters;
		getCsv({
			ids: included,
			filters: filteredFilters,
			excluded_ids: excluded,
			top: selectedCount || undefined,
			is_full_profiles: isFull,
			filters_text: getFiltersText(filteredFilters)
		});
	};

	const handleExportGoogleSheets = (isFull: boolean) => {
		// Analytics.trackGoogleSheetsExport(count, 'Search');
		const { technographics_page, page, size, ...filteredFilters } = filters;
		getGoogleSheetsUrl({
			ids: included,
			filters: filteredFilters,
			excluded_ids: excluded,
			top: selectedCount || undefined,
			is_full_profiles: isFull
		});
	};

	useEffect(() => {
		const saved = localStorage.getItem('technographicsCompanyParams');
		if (saved) {
			localStorage.removeItem('technographicsCompanyParams');
			try {
				getGoogleSheetsUrl(JSON.parse(saved));
			} catch (parseErr) {
				console.error('Failed to parse saved export params:', parseErr);
			}
		}
	}, []);

	if (isFetching) return <ResultsSkeleton />;

	if (isError || !data) return <ResultsError />;

	if (data.results.length === 0)
		return (
			<div className={styles.errorWrapper}>
				<ResultsEmpty company={true} />
			</div>
		);

	return (
		<section className={styles.container}>
			<Table className={styles.list} ref={ref}>
				<div className={cn(styles.header, { [styles.headerBorder]: !isFree })}>
					<div className={styles.listControl}>
						<TableSelectionV2
							controls={{
								main: { togglePage, count, isAllSelected },
								extra: { selectPage, selectAll, selectCount, clearSelection }
							}}
							hint="Select"
							total={data.results_count}
						/>
						<div className={styles.infoButtons}>
							<Pagination
								page={filters.technographics_page!}
								onChange={p => search({ ...filters, technographics_page: p })}
								onPageCount={data.results.length}
								total={data.results_count}
							/>
							<SelectProfileTooltip isDisabled={!someSelected}>
								<span>
									<TechnographicsExportToPushMenu
										disabled={!someSelected}
										handleExportCsv={handleExportCsv}
										handleExportGoogleSheets={handleExportGoogleSheets}
										candidates={{
											count,
											ids: included,
											excluded_ids: excluded,
											top: selectedCount || undefined,
											filters: filters
										}}
										type="companies"
										setProgress={setProgress}>
										<Button type="blue" disabled={!someSelected}>
											{'Export to >'}
										</Button>
									</TechnographicsExportToPushMenu>
								</span>
							</SelectProfileTooltip>
						</div>
					</div>
				</div>
				<div className={styles.card}>
					{data.results.map((candidate, idx) => (
						<CompanyRow
							checkbox={{
								checked: isSelected({ row: candidate.profile, page: filters.page - 1, idx }),
								onChange: () => toggle({ row: candidate.profile, page: filters.page - 1, idx }),
								mode
							}}
							key={candidate.profile.id}
							onClick={() => setCandidate(candidate)}
							candidate={candidate.profile}
							meta={candidate.match_metadata}
						/>
					))}
				</div>
				<div className={styles.footer}>
					<div className={styles.footerText}>
						<Text variant="inter/13/regular" color="darker_grey">
							End of page
						</Text>
					</div>
					<div className={styles.footerPagination}>
						<Pagination
							page={filters.technographics_page}
							onChange={p => search({ ...filters, technographics_page: p })}
							onPageCount={data.results.length}
							total={data.results_count}
						/>
					</div>
				</div>
			</Table>
			{candidate && (
				<CompanyModal
					candidates={data.results.map(candidate => candidate)}
					setCandidate={setCandidate}
					candidate={candidate}
					onClose={closeCandidateModal}
					total={data.results_count}
					page={filters.page}
					onChange={p => search({ ...filters, page: p })}
				/>
			)}
		</section>
	);
};
